"use client";

import { useRef } from "react";
import { toast } from "sonner";
import { sourced, unavailable } from "@/components/ui/transaction-review-contract";
import { cancelListing, confirmListingStep } from "@/features/finance/api";
import { classifyChainPost, readChainWorkId } from "@/features/finance/chain-settlement";
import { trackChainWork } from "@/features/finance/chain-work-dock";
import { currencyStateReason, useNetworkCurrencies } from "@/features/finance/currency-balance";
import { useExternalStepHandoff } from "@/features/finance/hooks/useExternalStepHandoff";
import { refreshQuietly } from "@/features/finance/place-offer";
import { toastApiError } from "@/lib/api-toast";
import { getErrorMessage } from "@/lib/error-message";
import { formatDecimal, multiplyDecimalString, parseApiAmount } from "@/lib/decimal-amount";
import type { TransactionRecord } from "@/lib/transaction-flow";
import { buildReviewRows } from "../review-rows";
import type { FlowModel, SharedFlowProps } from "./shared";
export type CancelListingProps = SharedFlowProps & {
  networkName: string;
  networkId?: number;
  fallbackSymbol: string;
  listingPrice: number | null;
  listingQuantity: number | null;
  listingCurrencyAddress: string | null;
  listingCurrencySymbol: string | null;
  onClose: () => void;
  onSuccess: () => void;
};

/**
 * CANCEL THE LISTING — the legacy `CancelListingFlow`, moved verbatim: the
 * listing's currency named from its token address (the page's symbol is the
 * NETWORK label), a scientific-notation price tolerated (`parseApiAmount`),
 * the external wallet's round against the same `listing_id` (never a second
 * cancel), and a queued chain leg handed to the dock — the listing can still
 * be bought until the chain says otherwise, and the words say so.
 */
export function useCancelListing(p: CancelListingProps) {
  const {
    lang,
    assetName,
    assetImage,
    collectionName,
    tokenId,
    networkLabel,
    royaltyPercent,
    contractAddress,
    onRecord,
    networkName,
    networkId,
    fallbackSymbol,
    listingPrice,
    listingQuantity,
    listingCurrencyAddress,
    listingCurrencySymbol,
    onClose,
    onSuccess
  } = p;
  const isFa = lang === "fa";
  const currencyState = useNetworkCurrencies(networkId);
  // Only engaged when the backend answers `LISTING_CANCEL_REQUIRED`.
  const handoff = useExternalStepHandoff(lang, "listing_id");
  const handedToDock = useRef(false);
  const listingCurrency = listingCurrencyAddress ? currencyState.currencies.find(c => String(c.contract_address ?? "").toLowerCase() === listingCurrencyAddress.toLowerCase()) : undefined;
  const quantity = listingQuantity && listingQuantity > 0 ? listingQuantity : 1;
  // `parseApiAmount`, not `String(...)`: `price_per_item` can be `"1.0E-14"`.
  const parsedUnitPrice = parseApiAmount(listingPrice);
  const unitPrice = parsedUnitPrice ? formatDecimal(parsedUnitPrice) : null;
  const total = unitPrice ? multiplyDecimalString(unitPrice, quantity) : null;
  const rows = buildReviewRows({
    lang,
    perspective: "seller",
    assetName,
    collectionName,
    tokenId,
    quantity,
    unitPrice,
    total,
    currencySymbol: listingCurrencySymbol ?? listingCurrency?.symbol?.toUpperCase() ?? null,
    currencyUnavailableReason: currencyStateReason(currencyState, isFa) ?? (isFa ? `ارز این لیست از روی آدرس قرارداد آن قابل تشخیص نبود. برچسب شبکه ${fallbackSymbol} است، نه نام ارز.` : `This listing's currency could not be matched from its token address. ${fallbackSymbol} is the network label, not a currency name.`),
    networkLabel,
    balance: null,
    balanceUnavailableReason: isFa ? "لغو لیست از موجودی شما برداشت نمی‌کند." : "Cancelling a listing does not draw on your balance.",
    royaltyPercent,
    expiration: unavailable(isFa ? "این لیست انقضایی ندارد؛ تا زمان فروش یا لغو باز می‌ماند." : "This listing has no expiry; it stays open until it sells or is cancelled."),
    conditions: sourced(isFa ? "پس از لغو، این NFT دیگر قابل خرید نیست. آفرهای ثبت‌شده مستقل از لیست هستند و با این کار لغو نمی‌شوند." : "After cancelling, the NFT can no longer be bought. Offers are independent of the listing and are not cancelled by this.")
  });
  const onConfirm = async (requestId: string): Promise<TransactionRecord | null> => {
    const success = (): TransactionRecord => ({
      requestId,
      flow: "cancel-listing",
      outcome: "success",
      title: isFa ? "لیست فروش لغو شد" : "Listing cancelled",
      description: isFa ? "این NFT دیگر برای فروش نمایش داده نمی‌شود." : "This NFT is no longer offered for sale.",
      at: new Date().toISOString()
    });
    const submitted = (): TransactionRecord => ({
      requestId,
      flow: "cancel-listing",
      outcome: "success",
      title: isFa ? "درخواست لغو ثبت شد" : "Cancellation submitted",
      description: isFa ? "تا تایید بلاکچین این NFT هنوز قابل خرید است. نتیجه در کادر گوشه‌ی صفحه دنبال می‌شود." : "Until the blockchain confirms, this NFT can still be bought. The card at the corner follows it.",
      at: new Date().toISOString()
    });
    // The server's reason, when it sent one, leads; the listing's state always follows.
    const failure = (reason?: string): TransactionRecord => ({
      requestId,
      flow: "cancel-listing",
      outcome: "failure",
      title: isFa ? "لغو لیست انجام نشد" : "The listing was not cancelled",
      description: `${reason ? `${reason} ` : ""}${isFa ? "لیست فروش همچنان فعال است. می‌توانید دوباره تلاش کنید." : "The listing is still active. You can try again."}`,
      at: new Date().toISOString()
    });
    try {
      // Later presses, once the backend handed the transaction over: sign and
      // confirm against the `listing_id` it already gave us. Never a second `cancel`.
      if (handoff.pending) {
        const result = await handoff.sign((listingId, payload) => confirmListingStep(contractAddress, tokenId, listingId, payload));
        if (!result) return null;
        if (result.outcome === "pending_confirmation") return null;
        toast.success(isFa ? "لیست فروش لغو شد" : "Listing cancelled");
        await refreshQuietly(onSuccess);
        return success();
      }
      const response = await cancelListing(contractAddress, tokenId, {
        network: networkName
      });
      const outcome = handoff.receive(response);
      if (outcome.kind === "handoff") return null;
      if (outcome.kind === "unusable") {
        toast.error(isFa ? "لغو لیست فروش ناموفق بود" : "Failed to cancel listing");
        return failure();
      }

      // `SUBMIT_CANCEL_LISTING`: registered, not done — the listing is still live.
      if (classifyChainPost(response, "cancelListing") === "queued") {
        handedToDock.current = true;
        trackChainWork({
          mode: "cancelListing",
          title: assetName,
          image: assetImage,
          contractAddress,
          tokenId,
          lang,
          workId: readChainWorkId(response, "cancelListing")
        });
        await refreshQuietly(onSuccess);
        toast.success(isFa ? "درخواست لغو ثبت شد؛ در انتظار بلاکچین" : "Cancellation submitted — waiting for the blockchain");
        return submitted();
      }
      toast.success(isFa ? "لیست فروش لغو شد" : "Listing cancelled");
      await refreshQuietly(onSuccess);
      return success();
    } catch (error) {
      toastApiError(error, isFa ? "لغو لیست فروش ناموفق بود" : "Failed to cancel listing");
      // The receipt carries the server's own reason where it sent one.
      return failure(getErrorMessage(error, "") || undefined);
    }
  };
  const model: FlowModel = {
    lang,
    title: isFa ? "لغو لیست فروش" : "Cancel listing",
    rows,
    irreversibility: isFa ? "لغو لیست قابل بازگرداندن نیست؛ برای فروش دوباره باید NFT را از نو لیست کنید." : "Cancelling cannot be undone — to sell again you would have to create a new listing.",
    // Once the wallet owes a round the button is doing a different job.
    confirmLabel: handoff.continueLabel ?? (isFa ? "لغو لیست فروش" : "Cancel listing"),
    confirmConsequence: isFa ? "این لیست بازگردانده نمی‌شود" : "this listing cannot be restored",
    confirmColor: "danger",
    confirmLoadingLabel: handoff.pending ? isFa ? "در انتظار کیف پول شما..." : "Waiting for your wallet…" : undefined,
    blockedReason: handoff.blockedReason,
    hasInput: false,
    handoff,
    networkLabel,
    onConfirm,
    closeOnSuccess: () => handedToDock.current,
    onRecord,
    onClose: handoff.guardClose(onClose)
  };
  return {
    model
  };
}